/**
 * Analyze tab — Spaces & Zones panel tools (BIM "Spaces & Zones").
 *
 * Spaces are volumetric analysis regions independent of Rooms: they drive
 * heating/cooling and lighting load calculations. Zones group spaces served
 * by the same AHU/thermostat. Separators split open areas without walls.
 *
 * Elements created:
 * - category 'Spaces'          (planar boundary + height, rendered as a
 *                               dashed blue volume with a green tag bubble)
 * - category 'Zones'           (groups space ids, rendered as a merged
 *                               dashed region)
 * - category 'Space Separators' (2-point divider line)
 */

import { v4 as uuidv4 } from 'uuid';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import { BaseTool } from '../BaseTool';
import { useAnalysisStore } from '../../state/useAnalysisStore';
import i18n from '../../i18n';

const SPACE_HEIGHT_DEFAULT = 3;

export class SpaceTool extends BaseTool {
    id = 'ANALYZE_SPACE';
    name = 'Space';
    cursor = 'crosshair';

    private points: THREE.Vector3[] = [];

    onActivate(): void {
        this.notify(i18n.t('tools.notify.clickTheSpaceBoundaryCornersOr'), 'info');
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context || event.button !== 0) return;
        const point = this.getIntersection(event);
        if (!point) return;
        const snapped = this.snapToGrid(point);

        // Near-first-point closes the boundary loop.
        if (this.points.length >= 3 && snapped.distanceTo(this.points[0]) < 0.3) {
            this.createSpace(this.points.slice());
            this.reset();
            return;
        }
        this.points.push(snapped);
        this.updatePreview(snapped);
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context || this.points.length === 0) return;
        const point = this.getIntersection(event);
        if (point) this.updatePreview(this.snapToGrid(point));
    }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Enter' && this.points.length > 0) {
            if (this.points.length < 3) {
                // Single click: synthesize a small default boundary around it.
                const c = this.points[0];
                const auto: THREE.Vector3[] = [
                    new THREE.Vector3(c.x - 2, c.y - 2, c.z),
                    new THREE.Vector3(c.x + 2, c.y - 2, c.z),
                    new THREE.Vector3(c.x + 2, c.y + 2, c.z),
                    new THREE.Vector3(c.x - 2, c.y + 2, c.z),
                ];
                this.createSpace(auto);
            } else {
                this.createSpace(this.points.slice());
            }
            this.reset();
            return;
        }
        super.onKeyDown(event);
    }

    private reset(): void {
        this.points = [];
        this.clearPreview();
    }

    private updatePreview(current: THREE.Vector3): void {
        this.context?.appStore.setToolPreview({
            type: 'polygon',
            points: [...this.points, current],
            color: '#2f6fb8',
        });
    }

    private createSpace(points: THREE.Vector3[]): void {
        const ctx = this.context;
        if (!ctx) return;
        const height = ctx.bimStore.toolProperties.spaceHeight ?? SPACE_HEIGHT_DEFAULT;
        const manualName = ctx.bimStore.toolProperties.spaceName;
        const levelZ = points[0].z;
        const boundary = points.map(p => [p.x, p.y] as [number, number]);
        const area = polygonAreaXY(boundary);
        const index = countSpaces(ctx.bimStore.project.elements) + 1;
        const name = manualName && manualName.trim() ? manualName.trim() : `Space ${index}`;

        ctx.bimStore.addElement({
            id: uuidv4(),
            category: 'Spaces',
            type: 'Space',
            name,
            parameters: {
                'Name': name,
                'Number': String(500 + index),
                'Area': `${area.toFixed(2)} m²`,
                'Volume': `${(area * height).toFixed(2)} m³`,
                'Level': '',
                'Level Base': levelZ,
                'Height': height,
                'Exposure': 0.35,
                'Glazing Ratio': 0.35,
                'Department': 'General',
            },
            constraints: [],
            geometryData: {
                boundary,
                levelZ,
                height,
                position: [points[0].x, points[0].y, levelZ],
            },
        } as any);
        useAnalysisStore.getState().bumpSpaces();
        this.notify(i18n.t('tools.notify.spaceCreated', { name: name, area: area.toFixed(1), height: height }), 'success');
    }

    onPointerUp(): void { }
}

export class ZoneTool extends BaseTool {
    id = 'ANALYZE_ZONE';
    name = 'Zone';
    cursor = 'crosshair';

    private picked: string[] = [];

    onActivate(): void {
        this.picked = [];
        this.notify(i18n.t('tools.notify.clickSpacesToAddThemTo'), 'info');
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context || event.button !== 0) return;
        const hit = this.findElementHitAtPoint(event, el => (el as any).category === 'Spaces');
        if (!hit) {
            this.notify(i18n.t('tools.notify.clickOnASpaceVolume'), 'warning');
            return;
        }
        const id = (hit.element as any).id;
        if (this.picked.includes(id)) {
            this.picked = this.picked.filter(p => p !== id);
        } else {
            this.picked.push(id);
        }
        this.notify(i18n.t('tools.notify.zonePicked', { count: this.picked.length }), 'info');
    }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Enter') {
            this.createZone();
            return;
        }
        super.onKeyDown(event);
    }

    private createZone(): void {
        const ctx = this.context;
        if (!ctx) return;
        if (this.picked.length < 1) {
            this.notify(i18n.t('tools.notify.selectAtLeastOneSpaceFirst'), 'warning');
            return;
        }
        const names = this.picked
            .map(id => ctx.bimStore.project.elements.get(id))
            .filter(Boolean)
            .map(el => String((el as any).parameters?.['Name'] ?? ''));
        const index = countElements(ctx.bimStore.project.elements, 'Zones') + 1;
        ctx.bimStore.addElement({
            id: uuidv4(),
            category: 'Zones',
            type: 'HVAC Zone',
            name: `Zone ${index}`,
            parameters: {
                'Name': `Zone ${index}`,
                'Spaces': this.picked.length,
                'Space Names': names.join(', '),
                'System Type': 'Constant Volume',
            },
            constraints: this.picked.map(id => ({ type: 'ZONE_MEMBER', targetId: id } as any)),
            geometryData: { spaceIds: this.picked },
        } as any);
        useAnalysisStore.getState().bumpSpaces();
        this.notify(i18n.t('tools.notify.zoneCreated', { index: index, count: this.picked.length }), 'success');
        this.picked = [];
        ctx.appStore.setActiveTool('select');
    }

    onPointerMove(): void { }
    onPointerUp(): void { }
}

export class SpaceSeparatorTool extends BaseTool {
    id = 'ANALYZE_SPACE_SEPARATOR';
    name = 'Space Separator';
    cursor = 'crosshair';

    private startPoint: THREE.Vector3 | null = null;

    onActivate(): void {
        this.notify(i18n.t('tools.notify.clickTwoPointsToDivideOpen'), 'info');
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context || event.button !== 0) return;
        const point = this.getIntersection(event);
        if (!point) return;
        const snapped = this.snapToGrid(point);
        if (!this.startPoint) {
            this.startPoint = snapped;
            this.context.appStore.setToolPreview({
                type: 'line',
                start: snapped,
                end: snapped,
                color: '#2e9e44',
            });
            return;
        }
        this.context.bimStore.addElement({
            id: uuidv4(),
            category: 'Space Separators',
            type: 'Space Separator',
            name: 'Space Separator',
            parameters: {},
            constraints: [],
            geometryData: {
                start: [this.startPoint.x, this.startPoint.y, this.startPoint.z],
                end: [snapped.x, snapped.y, snapped.z],
            },
        } as any);
        useAnalysisStore.getState().bumpSpaces();
        this.notify(i18n.t('tools.notify.spaceSeparatorAddedAdjacentSpacesWill'), 'success');
        this.startPoint = null;
        this.clearPreview();
        this.context.appStore.setActiveTool('select');
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context || !this.startPoint) return;
        const point = this.getIntersection(event);
        if (!point) return;
        const snapped = this.snapToGrid(point);
        this.context.appStore.setToolPreview({
            type: 'line',
            start: this.startPoint,
            end: snapped,
            color: '#2e9e44',
        });
    }

    onPointerUp(): void { }
}

/** Toggle Spaces/Zones overlay visibility. */
export class ShowSpacesTool extends BaseTool {
    id = 'ANALYZE_SHOW_SPACES';
    name = 'Show Spaces';
    cursor = 'default';

    onActivate(): void {
        const store = useAnalysisStore.getState();
        store.toggleSpaces();
        this.notify(i18n.t('tools.notify.spacesDisplayToggled', { state: store.showSpaces ? i18n.t('tools.notify.stateDisabled') : i18n.t('tools.notify.stateEnabled') }), 'info');
        this.context?.appStore.setActiveTool('select');
    }

    onPointerDown(): void { }
    onPointerMove(): void { }
    onPointerUp(): void { }
}

// ==================== Helpers ====================

function polygonAreaXY(pts: [number, number][]): number {
    let a = 0;
    for (let i = 0; i < pts.length; i++) {
        const [x1, y1] = pts[i];
        const [x2, y2] = pts[(i + 1) % pts.length];
        a += x1 * y2 - x2 * y1;
    }
    return Math.abs(a / 2);
}

function countSpaces(elements: Map<string, any>): number {
    return countElements(elements, 'Spaces');
}

function countElements(elements: Map<string, any>, category: string): number {
    let n = 0;
    elements.forEach(el => { if (el.category === category) n++; });
    return n;
}
